import { Room } from 'livekit-client';
import { action, makeAutoObservable, observable } from 'mobx';
import { RoomMetadata, RoomModeratorRaw, WebinarModeData, WebinarSpeaker } from 'api/types';

interface RoomInfoStoreInterface {
    room: Room | null;
    roomId: string;
    deltaWithServerTime: number;
}

class RoomInfoStore implements RoomInfoStoreInterface {
    @observable public hasRecorder = false;
    @observable public room: RoomInfoStoreInterface['room'] = null;
    @observable public roomId: RoomInfoStoreInterface['roomId'] = '';
    @observable public deltaWithServerTime: RoomInfoStoreInterface['deltaWithServerTime'] = 0;
    @observable public isStatisticVisible = false;
    @observable public metadata: RoomMetadata | null = null;
    @observable public isPreJoin: boolean = false;

    public constructor() {
        makeAutoObservable(this);
    }

    @action
    public setHasRecorder = (hasRecorder: boolean) => {
        this.hasRecorder = hasRecorder;
    };

    @action
    public setRoom = (room: Room | null) => {
        this.room = room;
    };

    @action
    public setRoomId = (id: string) => {
        this.roomId = id;
    };

    @action
    public setDeltaWithServerTime = (delta: number) => {
        this.deltaWithServerTime = delta;
    };

    @action
    public setStatisticVisibility = (isVisible: boolean) => {
        this.isStatisticVisible = isVisible;
    };

    @action
    public setMetadata = (metadata: RoomMetadata | null) => {
        this.metadata = metadata;
    };

    @action
    public setIsPreJoin = (flag: boolean) => {
        this.isPreJoin = flag;
    };

    /** Сброс metadata при выходе из комнаты — иначе waitingUsers «залипают» на prejoin. */
    @action
    public clearMetadata = () => {
        this.metadata = null;
    };

    /**
     * Оптимистично добавляет модератора в локальный metadata (чтобы бейдж «Модератор» появился сразу).
     * USER: userId задан, identity null. GUEST: identity задан (LiveKit participant.identity).
     */
    @action
    public addModeratorToMetadata = (userId: string | null, identity: string | null) => {
        const current = this.metadata ?? {};
        const list = Array.isArray(current.moderators) ? [...current.moderators] : [];
        const already = list.some(
            (m) => (userId && m.userId === userId) || (identity != null && m.identity === identity),
        );
        if (!already) {
            const role = 'moderator';
            const entry: RoomModeratorRaw = userId
                ? { type: 'USER', userId, identity: null, role }
                : { type: 'GUEST', userId: null, identity: identity ?? undefined, role };
            list.push(entry);
            this.metadata = { ...current, moderators: list };
        }
    };

    /**
     * Оптимистично убирает модератора из локального metadata.
     */
    @action
    public removeModeratorFromMetadata = (userId: string | null, identity: string | null) => {
        const current = this.metadata ?? {};
        const list = Array.isArray(current.moderators) ? current.moderators : [];
        const next = list.filter(
            (m) => !((userId && m.userId === userId) || (identity != null && m.identity === identity)),
        );
        if (next.length !== list.length) {
            this.metadata = { ...current, moderators: next };
        }
    };

    /** Оптимистично обновляет весь объект webinarMode в metadata. */
    @action
    public setWebinarMode = (webinarMode: WebinarModeData) => {
        const current = this.metadata ?? {};
        this.metadata = { ...current, webinarMode };
    };

    /** Оптимистично добавляет спикера в webinarMode.speakers. */
    @action
    public addSpeakerToWebinarMetadata = (speaker: WebinarSpeaker) => {
        const current = this.metadata ?? {};
        const existing = current.webinarMode ?? {};
        const speakers = Array.isArray(existing.speakers) ? [...existing.speakers] : [];
        const alreadyExists = speakers.some(
            (s) =>
                (speaker.userId && s.userId === speaker.userId) ||
                (speaker.identity && s.identity === speaker.identity),
        );
        if (!alreadyExists) {
            this.metadata = { ...current, webinarMode: { ...existing, speakers: [...speakers, speaker] } };
        }
    };

    /** Оптимистично убирает спикера из webinarMode.speakers. */
    @action
    public removeSpeakerFromWebinarMetadata = (speaker: WebinarSpeaker) => {
        const current = this.metadata ?? {};
        const existing = current.webinarMode ?? {};
        const speakers = Array.isArray(existing.speakers) ? existing.speakers : [];
        const next = speakers.filter(
            (s) =>
                !(
                    (speaker.userId && s.userId === speaker.userId) ||
                    (speaker.identity && s.identity === speaker.identity)
                ),
        );
        if (next.length !== speakers.length) {
            this.metadata = { ...current, webinarMode: { ...existing, speakers: next } };
        }
    };
}

export const roomInfoStore = new RoomInfoStore();
